import {
  assertFeatureFlagVariants,
  listFeatureFlagDefinitions,
  listProductExperimentDefinitions,
} from '@galapagos/product-analytics';

import { experimentManifestSchema, type ExperimentManifest } from './schema';

export const exampleAgentDefinitionExperiment = experimentManifestSchema.parse({
  experiment_key: 'experiment-example-agent-definition-voice',
  version: '1.0.0',
  status: 'draft',
  owner: 'product_analytics',
  assignment_source: 'galapagos',
  flag_key: 'flag-example-agent-definition-voice',
  hypothesis: {
    id: 'clearer_guidance',
    owner: 'product_analytics',
    statement: 'Clearer guidance improves completion rate for eligible members.',
    expected_direction: 'increase',
    target_population: 'Eligible API members using the voice session.',
    decision_criteria: 'Ship when completion rate improves with neutral guardrails.',
  },
  primary_metric: {
    id: 'completion_rate',
    event: 'voice_session_completed',
    aggregation: 'conversion_rate',
    denominator: 'assigned_exposures',
    expected_direction: 'increase',
    exclusions: ['fallback_selection'],
    window_days: 14,
    minimum_sample_size: 400,
  },
  guardrail_metrics: [
    {
      id: 'early_exit_rate',
      event: 'voice_session_early_exit',
      aggregation: 'conversion_rate',
      denominator: 'assigned_exposures',
      expected_direction: 'decrease',
      exclusions: ['fallback_selection'],
      window_days: 14,
      minimum_sample_size: 400,
    },
  ],
  readout: {
    cadence: 'weekly',
    owner: 'product_analytics',
    primary_metric_id: 'completion_rate',
    guardrail_metric_ids: ['early_exit_rate'],
    query_template: 'experiment_readout_by_assignment',
  },
  targeting: {
    rollout_percent: 0,
    salt: 'example_agent_definition_voice_v1',
    platforms: ['api'],
  },
  variants: [
    {
      key: 'control',
      label: 'Current voice definition',
      allocation_percent: 50,
      control: true,
      targets: [
        {
          scope: 'agent_definition',
          agent_definition_id: 'current_voice_agent',
          agent_definition_version: '1.0.0',
        },
      ],
    },
    {
      key: 'variant-a',
      label: 'Alternative voice definition',
      allocation_percent: 50,
      targets: [
        {
          scope: 'agent_definition',
          agent_definition_id: 'alternative_voice_agent',
          agent_definition_version: '1.0.0',
        },
      ],
    },
  ],
});

export const exampleCoordinatedExperiment = experimentManifestSchema.parse({
  experiment_key: 'experiment-example-agent-and-genui-bundle',
  version: '1.0.0',
  status: 'draft',
  owner: 'product_analytics',
  assignment_source: 'galapagos',
  flag_key: 'flag-example-agent-and-genui-bundle',
  hypothesis: {
    id: 'coordinated_guidance',
    owner: 'product_analytics',
    statement: 'Coordinated guidance improves next step activation.',
    expected_direction: 'increase',
    target_population: 'Eligible API and app members seeing bundled guidance.',
    decision_criteria: 'Ship when activation improves with neutral guardrails.',
  },
  primary_metric: {
    id: 'activation_rate',
    event: 'next_step_activated',
    aggregation: 'conversion_rate',
    denominator: 'assigned_exposures',
    expected_direction: 'increase',
    exclusions: ['fallback_selection'],
    window_days: 21,
    minimum_sample_size: 500,
  },
  guardrail_metrics: [],
  readout: {
    cadence: 'weekly',
    owner: 'product_analytics',
    primary_metric_id: 'activation_rate',
    guardrail_metric_ids: [],
    query_template: 'experiment_bundle_readout',
  },
  targeting: {
    rollout_percent: 0,
    salt: 'example_agent_and_genui_bundle_v1',
    platforms: ['api', 'ios', 'android'],
  },
  variants: [
    {
      key: 'control',
      label: 'Current agent and surface',
      allocation_percent: 50,
      control: true,
      targets: [
        {
          scope: 'agent_definition',
          agent_definition_id: 'current_voice_agent',
          agent_definition_version: '1.0.0',
        },
        {
          scope: 'genui_surface',
          surface_id: 'current_summary_surface',
          schema_version: '1.0.0',
          screen_id: 'summary_screen',
        },
      ],
    },
    {
      key: 'variant-a',
      label: 'Alternative agent and surface',
      allocation_percent: 50,
      targets: [
        {
          scope: 'agent_definition',
          agent_definition_id: 'alternative_voice_agent',
          agent_definition_version: '1.0.0',
        },
        {
          scope: 'genui_surface',
          surface_id: 'alternative_summary_surface',
          schema_version: '1.0.0',
          screen_id: 'summary_screen',
        },
      ],
    },
  ],
});

export const analyticsDiagnosticsExperiment = experimentManifestSchema.parse({
  experiment_key: 'experiment-analytics-diagnostics',
  version: '1.0.0',
  status: 'ready',
  owner: 'product-analytics',
  assignment_source: 'posthog',
  flag_key: 'flag-analytics-diagnostics',
  hypothesis: {
    id: 'diagnostics-affordance-activation',
    owner: 'product-analytics',
    statement: 'A clearer diagnostics affordance increases successful internal activation.',
    expected_direction: 'increase',
    target_population: 'Authenticated internal users in development and preview environments.',
    decision_criteria:
      'Adopt the variant after sufficient activation with a neutral error guardrail.',
  },
  primary_metric: {
    id: 'analytics.diagnostics.activated',
    event: 'experiment_outcome',
    event_properties: [{ key: 'metric_key', value: 'analytics.diagnostics.activated' }],
    aggregation: 'conversion_rate',
    denominator: 'assigned-exposures',
    expected_direction: 'increase',
    exclusions: ['default-selection'],
    window_days: 14,
    minimum_sample_size: 100,
  },
  guardrail_metrics: [
    {
      id: 'analytics.diagnostics.failed',
      event: 'experiment_outcome',
      event_properties: [{ key: 'metric_key', value: 'analytics.diagnostics.failed' }],
      aggregation: 'conversion_rate',
      denominator: 'assigned-exposures',
      expected_direction: 'decrease',
      exclusions: ['default-selection'],
      window_days: 14,
      minimum_sample_size: 100,
    },
  ],
  readout: {
    cadence: 'weekly',
    owner: 'product-analytics',
    primary_metric_id: 'analytics.diagnostics.activated',
    guardrail_metric_ids: ['analytics.diagnostics.failed'],
    query_template: 'query-analytics-diagnostics-result',
  },
  targeting: {
    rollout_percent: 0,
    salt: 'experiment-analytics-diagnostics-v1',
    platforms: ['web'],
    internal_cohorts: ['atlas-internal'],
  },
  variants: [
    {
      key: 'control',
      label: 'Current diagnostics label',
      allocation_percent: 50,
      control: true,
      targets: [
        {
          scope: 'genui_surface_element',
          surface_id: 'analytics-diagnostics',
          schema_version: '1.0.0',
          screen_id: 'analytics/diagnostics',
          element_key: 'diagnostics-control',
        },
      ],
    },
    {
      key: 'variant',
      label: 'Prominent diagnostics label',
      allocation_percent: 50,
      targets: [
        {
          scope: 'genui_surface_element',
          surface_id: 'analytics-diagnostics',
          schema_version: '1.0.0',
          screen_id: 'analytics/diagnostics',
          element_key: 'diagnostics-variant',
        },
      ],
    },
  ],
});

export function parseProductExperimentDefinitions(
  definitions: readonly unknown[],
): ExperimentManifest[] {
  const flags = new Map<string, ReturnType<typeof listFeatureFlagDefinitions>[number]>(
    listFeatureFlagDefinitions().map((flag) => [flag.key, flag]),
  );
  return definitions.map((definition) => {
    const manifest = experimentManifestSchema.parse(definition);
    const flag = flags.get(manifest.flag_key);
    if (!flag) {
      throw new Error(`Experiment flag ${manifest.flag_key} is not registered`);
    }
    assertFeatureFlagVariants(
      flag,
      manifest.variants.map(({ key }) => key),
    );
    return manifest;
  });
}

const productExperimentManifests = parseProductExperimentDefinitions(
  listProductExperimentDefinitions(),
);

const experimentRegistry: readonly ExperimentManifest[] = Object.freeze([
  exampleAgentDefinitionExperiment,
  exampleCoordinatedExperiment,
  analyticsDiagnosticsExperiment,
  ...productExperimentManifests,
]);

export function listExperimentManifests(): readonly ExperimentManifest[] {
  return experimentRegistry;
}

export function findExperimentManifest(experimentKey: string): ExperimentManifest | undefined {
  return experimentRegistry.find((manifest) => manifest.experiment_key === experimentKey);
}
